import { Link } from 'react-router-dom'
import { Clock, LayoutDashboard } from 'lucide-react'
import { Card } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { PageHeader } from '@/components/common/PageHeader'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMyCompanies } from '@/features/companies/hooks/useCompanies'
import { useMyAttendanceToday } from '@/features/attendance/hooks/useAttendance'
import { usePermissions } from '@/hooks/usePermissions'
import {
  canAccessAttendanceSelfService,
} from '@/features/attendance/utils/attendanceEligibility'

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

export interface StaffDashboardProps {
  /** When true, only the session card is rendered (use with an outer PageShell). */
  omitPageHeader?: boolean
}

/** Attendance/session summary card for any non-admin self-service user. */
export function StaffDashboard({ omitPageHeader = false }: StaffDashboardProps): React.ReactElement {
  const user = useAuthStore((s) => s.user)
  const roleSlug = useAuthStore((s) => s.user?.roleSlug)
  const { can, isLoading: permissionsLoading } = usePermissions()
  const canReadAttendance = can('attendance', 'read_self')
  const showAttendanceWidget =
    canAccessAttendanceSelfService({ roleSlug, canRead: canReadAttendance }) &&
    !permissionsLoading
  const firstName = user?.firstName ?? user?.name?.split(' ')[0] ?? 'there'

  const { data: myCompanies = [] } = useMyCompanies()
  const companyNameById = new Map(myCompanies.map((c) => [c._id, c.name]))
  const { data: todayData, isLoading: todayLoading } = useMyAttendanceToday({
    enabled: showAttendanceWidget,
  })
  const entries = todayData?.data ?? []
  const allShifts = entries.flatMap((e) =>
    e.shifts.map((s) => ({ ...s, companyId: e.companyId })),
  )
  const activeShifts = allShifts.filter((s) => s.attendance?.isActive)
  const completedShifts = allShifts.filter(
    (s) => s.attendance?.dayCompleted === true,
  )

  return (
    <div className="space-y-6">
      {!omitPageHeader && (
        <PageHeader
          icon={LayoutDashboard}
          title={`${getGreeting()}, ${firstName}`}
          description="View your attendance, shifts, and leave requests."
        />
      )}

      {showAttendanceWidget && myCompanies.length > 0 && (
        <Card
          header={
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" aria-hidden />
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Today&apos;s session
                </span>
              </div>
              <Link
                to="/my-attendance"
                className="text-xs font-medium text-primary transition-colors hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                View full attendance
              </Link>
            </div>
          }
        >
          {todayLoading ? (
            <SectionLoader variant="list" rows={2} label="Loading today's shifts" />
          ) : activeShifts.length === 0 && completedShifts.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <div className="rounded-full bg-gray-100 p-3 dark:bg-gray-700">
                <Clock className="h-6 w-6 text-gray-400 dark:text-gray-500" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Not clocked in yet
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Use the status pill in the header or open My Attendance to clock in.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {activeShifts.map((s) => {
                const a = s.attendance!
                const name =
                  companyNameById.get(s.companyId) ?? 'Company'
                const shiftRef =
                  s.assignment?.shiftId &&
                  typeof s.assignment.shiftId === 'object'
                    ? (s.assignment.shiftId as { name?: string })
                    : null
                const shiftName = shiftRef?.name
                return (
                  <div
                    key={s.shiftAssignmentId}
                    className="flex items-center gap-2"
                  >
                    <span
                      className={
                        a.isOnBreak
                          ? 'h-2 w-2 rounded-full bg-amber-500'
                          : 'h-2 w-2 rounded-full bg-green-500'
                      }
                      aria-hidden
                    />
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {a.isOnBreak ? 'On break' : 'On shift'} at{' '}
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {name}
                      </span>
                      {shiftName && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {' '}
                          — {shiftName}
                        </span>
                      )}
                      {a.elapsedSeconds != null && !a.isOnBreak && (
                        <> · {formatElapsed(a.elapsedSeconds)}</>
                      )}
                    </p>
                  </div>
                )
              })}
              {completedShifts.map((s) => {
                const a = s.attendance!
                const name =
                  companyNameById.get(s.companyId) ?? 'Company'
                const shiftRef =
                  s.assignment?.shiftId &&
                  typeof s.assignment.shiftId === 'object'
                    ? (s.assignment.shiftId as { name?: string })
                    : null
                const shiftName = shiftRef?.name
                return (
                  <div
                    key={s.shiftAssignmentId}
                    className="flex items-center gap-2"
                  >
                    <span
                      className="h-2 w-2 rounded-full bg-gray-400"
                      aria-hidden
                    />
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Day complete at{' '}
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {name}
                      </span>
                      {shiftName && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {' '}
                          — {shiftName}
                        </span>
                      )}
                      {' '}· {formatElapsed((a.totalWorkedMinutes ?? 0) * 60)}
                    </p>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
